/**
 * Chat types for in-game messaging
 */

export interface ChatMessage {
  id: string;
  roomId: string;
  senderColor: 'white' | 'black';
  senderUsername: string;
  message: string;
  timestamp: number;
  isSystem?: boolean;
  /**
   * For preset phrases, from a guest or a registered player alike: the
   * canonical preset key (e.g. 'goodGame').
   * When present, clients should render the localized phrase via the i18n
   * key `chat.presets.<presetKey>` instead of the raw `message` text.
   */
  presetKey?: string;
  /**
   * «L'ha scritto chi lo sta ricevendo». UNICO campo del messaggio che NON e'
   * uguale per i due giocatori: lo calcola il server all'atto della consegna,
   * sull'identita' dell'autore, e per lo stesso `id` arriva `true` a uno e
   * `false` all'altro.
   *
   * Esiste perche' il COLORE non e' un'identita': la rivincita riusa la stessa
   * stanza e SCAMBIA i colori (`room.resetForNewGame`), mentre la conversazione
   * resta. Deducendo l'autore dal colore — com'era — ogni messaggio della
   * partita precedente cambiava lato: i propri comparivano come quelli
   * dell'avversario, e viceversa. Vale anche per il ritorno di un match a due
   * manche, e al rientro peggio ancora: il trascritto porta il colore di
   * allora, quindi la cronologia ripristinata risultava meta' invertita.
   *
   * Assente quando il server non sa per chi sta preparando il messaggio: vedi
   * `eMessaggioMio`, che in quel caso ripiega sul colore.
   */
  isOwn?: boolean;
}

export interface SendChatPayload {
  roomId: string;
  message: string;
}

/** Chat configuration constants */
export const CHAT_CONFIG = {
  /** Maximum message length in characters */
  MAX_MESSAGE_LENGTH: 200,
  /**
   * Limite A RAFFICA (4 ottobre 2026): al massimo `RATE_LIMIT_MAX_MESSAGES`
   * messaggi in una finestra mobile di `RATE_LIMIT_WINDOW_MS`.
   *
   * Prima era un messaggio ogni 1,5 secondi, e colpiva l'uso normale: «ciao» e
   * subito «come va?», o due emoji toccate di fila. Il limite resta perche'
   * protegge la segnalazione — la stanza tiene solo gli ultimi
   * `MAX_MESSAGES_PER_ROOM`, e senza tetto chi insulta potrebbe spingerli fuori
   * prima che l'altro prema «Segnala» — ma lascia passare due o tre messaggi di
   * fila, e per arrivare a cento ci vogliono piu' di tre minuti.
   */
  RATE_LIMIT_MAX_MESSAGES: 5,
  RATE_LIMIT_WINDOW_MS: 10_000,
  /** Maximum messages to keep in memory per room */
  MAX_MESSAGES_PER_ROOM: 100,
} as const;

/** L'esito del controllo a raffica: vedi `controllaRafficaChat`. */
export interface RafficaChat {
  /** Il messaggio puo' partire adesso. */
  ammesso: boolean;
  /** Gli invii ancora dentro la finestra (senza quello di adesso). */
  recenti: number[];
  /** Fra quanti millisecondi si libera un posto (0 se `ammesso`). */
  attesaMs: number;
}

/**
 * Il limite a raffica, una regola sola per server e schermo.
 *
 * Pura: riceve gli istanti degli invii precedenti e l'istante di adesso, butta
 * quelli usciti dalla finestra e dice se c'e' posto. Chi la chiama, se il
 * messaggio parte, aggiunge `adesso` a `recenti` e se li tiene per la volta
 * dopo. Il server la usa per rifiutare, lo schermo per non inviare un
 * messaggio che verrebbe rifiutato — e che, rifiutato, sparirebbe.
 */
export function controllaRafficaChat(invii: readonly number[], adesso: number, margineMs = 0): RafficaChat {
  // `margineMs` allarga la finestra: lo usa lo schermo, che misura all'invio
  // mentre il server misura all'arrivo. Il server la usa senza margine.
  const finestra = CHAT_CONFIG.RATE_LIMIT_WINDOW_MS + margineMs;
  const recenti = invii.filter((t) => adesso - t < finestra);
  if (recenti.length < CHAT_CONFIG.RATE_LIMIT_MAX_MESSAGES) {
    return { ammesso: true, recenti, attesaMs: 0 };
  }
  const piuVecchio = Math.min(...recenti);
  return { ammesso: false, recenti, attesaMs: finestra - (adesso - piuVecchio) };
}

/** Il codice di `game:error` quando il server rifiuta per il limite a raffica. */
export const CHAT_RATE_LIMITED = 'CHAT_RATE_LIMITED';

/**
 * Il codice di `game:error` quando la chat non c'e' piu': la stanza e' stata
 * raccolta, e con lei la conversazione.
 *
 * Serve perche' il rifiuto deve arrivare ALLA CHAT. Il server rispondeva
 * `{ message: 'Room not found' }` senza codice, e `chiaveErroreOnline` risolve
 * per CODICE e mai per messaggio: il rifiuto ripiegava sull'avviso generico
 * «Qualcosa non ha funzionato» della partita normale — dietro l'overlay di fine
 * partita, quindi invisibile — e in torneo e in arena su nessun avviso. Il
 * messaggio spariva e nessuno diceva perche'.
 *
 * E' la rete di sicurezza del messaggio GIA' IN VOLO quando la stanza muore:
 * l'avviso vero e proprio e' `game:chat-closed`, annunciato dal server.
 */
export const CHAT_ROOM_CLOSED = 'CHAT_ROOM_CLOSED';

// ============================================================================
// Chat presets and guest restrictions
//
// The curated emoji and preset phrases are available to EVERYONE: guests get
// them as their only composer, registered users get them in a drawer next to
// the free-text input (one tap instead of the on-screen keyboard). The point
// of a preset is that it travels as a key and is rendered in each reader's own
// language, so a Dutch player's greeting arrives translated to an Italian one.
//
// What is guest-only is the RESTRICTION: a guest may send nothing else.
// These constants and pure helpers are the single source of truth shared by
// the client (to render the palette) and the server (to enforce the policy).
// ============================================================================

/** Curated emoji set any player may send during a game. */
export const CHAT_EMOJIS = [
  '\u{1F44B}', // 👋 wave
  '\u{1F44D}', // 👍 thumbs up
  '\u{1F44F}', // 👏 clap
  '\u{1F642}', // 🙂 slight smile
  '\u{1F606}', // 😆 laughing
  '\u{1F62E}', // 😮 surprised
  '\u{1F622}', // 😢 crying
  '\u{1F914}', // 🤔 thinking
  '\u{1F525}', // 🔥 fire
  '\u{1F91D}', // 🤝 handshake
  '\u{1F64F}', // 🙏 thanks
  '\u{1F60E}', // 😎 cool
  '\u{1F609}', // 😉 wink
  '\u{2B50}', // ⭐ star
  '\u{1F4AA}', // 💪 strong
] as const;
export type ChatEmoji = (typeof CHAT_EMOJIS)[number];

/** A preset phrase any player may send. */
export interface ChatPreset {
  /** Stable transport id and i18n key suffix (`chat.presets.<key>`). */
  key: string;
}

/**
 * Preset phrases, in the order they are offered: greetings first, farewells last.
 *
 * The keys are transport ids, not copy: they are persisted with every reported
 * message (see chat-report.service) and must stay stable even when the wording
 * changes. `goodLuck` is the pre-game wish slot — Italian renders it as
 * "Buon gioco!", each language picks its own idiom.
 */
export const CHAT_PRESETS: readonly ChatPreset[] = [
  { key: 'hello' },
  { key: 'goodLuck' },
  { key: 'haveFun' },
  { key: 'goodGame' },
  { key: 'untilNext' },
  { key: 'thanks' },
] as const;

/**
 * Il preset che vale come SALUTO D'APERTURA, cioe' l'unico che una partita mostra
 * fuori dalla chat, come pastiglia sopra la barra dei comandi.
 *
 * Una chiave sola e non un insieme (`hello`, `haveFun`...): il popup deve essere
 * prevedibile, e le altre frasi restano quello che sono gia', righe di chat. Vive
 * qui e non nel web perche' la chiave di trasporto non va scritta due volte in due
 * package — il server la tagga (`applyChatGate`), il client la riconosce.
 */
export const SALUTO_PRESET_KEY = 'goodLuck';

/** Wire prefix marking a preset message, e.g. `preset:hello`. */
export const PRESET_PREFIX = 'preset:';

/**
 * Read a `preset:<key>` token, returning the key only if the preset exists.
 * Anything else — free text, an unknown key — is not a preset: `null`.
 *
 * Shared by both sides of the gate: it is what lets a registered user's tap on
 * a preset travel as a key (and be translated for the reader) instead of
 * arriving as the literal string "preset:goodGame".
 */
export function parsePresetToken(rawMessage: string): string | null {
  const msg = (rawMessage ?? '').trim();
  if (!msg.startsWith(PRESET_PREFIX)) return null;
  const key = msg.slice(PRESET_PREFIX.length);
  return CHAT_PRESETS.some((p) => p.key === key) ? key : null;
}

/** Result of validating a guest chat message against the allowed set. */
export type GuestChatValidation =
  | { ok: true; kind: 'emoji'; emoji: string }
  | { ok: true; kind: 'preset'; presetKey: string }
  | { ok: false };

/**
 * Validate a raw wire message sent by a guest.
 *
 * Accepts only:
 *  - a curated emoji, or
 *  - a `preset:<key>` token for a known preset.
 *
 * Everything else (free text, unknown emoji, unknown presets) is rejected.
 * This is the authoritative server-side gate; the client mirrors it.
 */
export function validateGuestMessage(rawMessage: string): GuestChatValidation {
  const msg = (rawMessage ?? '').trim();

  if (msg.startsWith(PRESET_PREFIX)) {
    const presetKey = parsePresetToken(msg);
    return presetKey ? { ok: true, kind: 'preset', presetKey } : { ok: false };
  }

  if ((CHAT_EMOJIS as readonly string[]).includes(msg)) {
    return { ok: true, kind: 'emoji', emoji: msg };
  }

  return { ok: false };
}

/** Input to the authoritative chat gate (combines guest policy + validation). */
export interface ChatGateInput {
  isGuest: boolean;
  message: string;
}

/** Outcome of the chat gate. `presetKey` is set only for guest preset phrases. */
export type ChatGateResult = { allowed: true; presetKey?: string } | { allowed: false };

/**
 * Authoritative chat policy used by the server.
 *
 * Registered users may send anything (length / rate limiting are enforced
 * separately by the handler); when what they send is a preset token it is
 * tagged as such, so it reaches every reader in their own language. Guests are
 * restricted to curated emoji or preset phrases.
 */
export function applyChatGate(input: ChatGateInput): ChatGateResult {
  if (!input.isGuest) {
    const presetKey = parsePresetToken(input.message);
    // An unknown `preset:` token from a registered user is not rejected: it is
    // just free text that happens to start with the prefix, and free text is
    // their right. It simply carries no key.
    return presetKey ? { allowed: true, presetKey } : { allowed: true };
  }

  const result = validateGuestMessage(input.message);
  if (!result.ok) {
    return { allowed: false };
  }
  if (result.kind === 'preset') {
    return { allowed: true, presetKey: result.presetKey };
  }
  return { allowed: true };
}

/**
 * Chi ha scritto questo messaggio: io, o l'altro?
 *
 * Unico predicato per tutti i punti del client — allineamento della bolla,
 * badge dei non letti, mute dopo una segnalazione, scelta del messaggio da
 * segnalare — perche' erano quattro deduzioni identiche e sbagliate nello
 * stesso modo (vedi `ChatMessage.isOwn`).
 *
 * Il ripiego sul colore vale per un client nuovo contro un server vecchio, il
 * tempo di un rilascio: e' esattamente il comportamento di prima, quindi non
 * peggiora niente, e sui messaggi della partita in corso e' anche giusto.
 */
export function eMessaggioMio(m: ChatMessage, playerColor: 'white' | 'black' | null): boolean {
  return m.isOwn ?? (m.senderColor === playerColor);
}
